// src/components/AudioPlayer.jsx - WITH DRAG-TO-SEEK FUNCTIONALITY
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Minus, SkipBack, SkipForward, Play, Pause,
  Volume2, VolumeX, Heart, ListPlus, ListMusic, ChevronDown
} from 'lucide-react';
import './AudioPlayer.css';

// ── localStorage helpers ─────────────────────────────────────────────────────
export const getLikes     = () => { try { return JSON.parse(localStorage.getItem('aura_likes') || '[]'); } catch { return []; } };
export const getPlaylists = () => { try { return JSON.parse(localStorage.getItem('aura_playlists') || '[]'); } catch { return []; } };
export const isLiked      = (id) => getLikes().some(s => s.id === id);

export const toggleLike = (song) => {
  const liked = getLikes();
  const exists = liked.find(s => s.id === song.id);
  const updated = exists ? liked.filter(s => s.id !== song.id) : [song, ...liked];
  localStorage.setItem('aura_likes', JSON.stringify(updated));
  return !exists;
};

export const addSongToPlaylist = (playlistId, song) => {
  const pls = getPlaylists();
  const updated = pls.map(pl => {
    if (pl.id !== playlistId) return pl;
    if (pl.songs.find(s => s.id === song.id)) return pl;
    return { ...pl, songs: [...pl.songs, song] };
  });
  localStorage.setItem('aura_playlists', JSON.stringify(updated));
  return updated;
};
// ────────────────────────────────────────────────────────────────────────────

const PlaylistDropdown = ({ song, onClose, onAdded }) => {
  const playlists = getPlaylists();

  const handleAdd = (pl) => {
    addSongToPlaylist(pl.id, song);
    onAdded(pl.name);
    onClose();
  };

  return ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }} onClick={onClose} />
      <div className="ap-add-portal">
        <p className="ap-add-menu-title">Add to playlist</p>
        {playlists.length === 0 ? (
          <p className="ap-add-menu-empty">No playlists yet.<br />Create one in Playlist page.</p>
        ) : (
          playlists.map(pl => (
            <button key={pl.id} className="ap-add-menu-item" onClick={() => handleAdd(pl)}>
              <ListMusic size={14} />
              <span>{pl.name}</span>
              <span className="ap-add-menu-count">{pl.songs.length}</span>
            </button>
          ))
        )}
      </div>
    </>,
    document.body
  );
};

// ────────────────────────────────────────────────────────────────────────────

const AudioPlayer = ({
  currentSong,
  songList = [],
  onNext,
  onPrevious,
  onClose,
  onMinimize,
  registerActions,
  onPlayStateChange,
}) => {
  const [isPlaying, setIsPlaying]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [volume, setVolume]           = useState(80);
  const [muted, setMuted]             = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(false);
  const [errorMsg, setErrorMsg]       = useState('');
  const [liked, setLiked]             = useState(false);
  const [showQueue, setShowQueue]     = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [toast, setToast]             = useState(null);
  const [playerReady, setPlayerReady] = useState(false);

  const playerRef    = useRef(null);
  const containerRef = useRef(null);
  const progressRef  = useRef(null);
  const intervalRef  = useRef(null);
  const retryRef     = useRef(0);
  const mountedRef   = useRef(true);
  const skipTimeoutRef = useRef(null);
  const playAttemptRef = useRef(0);
  const isDraggingRef  = useRef(false);

  const currentIndex = songList.findIndex(s => s.id === currentSong?.id);

  useEffect(() => {
    if (currentSong) {
      setLiked(isLiked(currentSong.id));
      // Reset error state when song changes
      setError(false);
      setErrorMsg('');
      setPlayerReady(false);
      retryRef.current = 0;
      playAttemptRef.current = 0;
    }
  }, [currentSong?.id]);

  // ── helpers ──────────────────────────────────────────────────────────────
  const safeSet = (fn) => { if (mountedRef.current) fn(); };

  const stopTracking = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  const startTracking = useCallback(() => {
    stopTracking();
    intervalRef.current = setInterval(() => {
      try {
        if (!playerRef.current?.getCurrentTime) return;
        const cur = playerRef.current.getCurrentTime();
        const tot = playerRef.current.getDuration();
        if (mountedRef.current && tot > 0 && !isDraggingRef.current) {
          setCurrentTime(cur); 
          setProgress((cur / tot) * 100); 
          setDuration(tot);
        }
      } catch (_) {}
    }, 500);
  }, [stopTracking]);

  // ✅ Auto-skip if taking too long to load
  const startSkipTimer = useCallback(() => {
    if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
    skipTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && loading) {
        setError(true);
        setErrorMsg('Loading timeout');
        setLoading(false);
        handleNext();
      }
    }, 10000); // 10 seconds timeout
  }, [loading]);

  // ── next / previous ──────────────────────────────────────────────────────
  const handleNext = useCallback(() => {
    if (!songList.length) return;
    if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
    safeSet(() => { 
      setLoading(true); 
      setError(false); 
      setErrorMsg('');
      setPlayerReady(false);
      retryRef.current = 0;
      playAttemptRef.current = 0;
    });
    stopTracking();
    const nextIndex = currentIndex < songList.length - 1 ? currentIndex + 1 : 0;
    const next = songList[nextIndex];
    onNext(next);
  }, [currentIndex, songList, onNext, stopTracking]);

  const handlePrevious = useCallback(() => {
    if (!songList.length) return;
    if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
    safeSet(() => { 
      setLoading(true); 
      setError(false); 
      setErrorMsg('');
      setPlayerReady(false);
      retryRef.current = 0;
      playAttemptRef.current = 0;
    });
    stopTracking();
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : songList.length - 1;
    const prev = songList[prevIndex];
    onPrevious(prev);
  }, [currentIndex, songList, onPrevious, stopTracking]);

  // ── register actions ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!registerActions) return;
    registerActions({
      play:     () => { 
        try { 
          if (playerRef.current && playerReady) {
            playerRef.current.playVideo(); 
          }
        } catch (_) {} 
      },
      pause:    () => { 
        try { 
          if (playerRef.current) {
            playerRef.current.pauseVideo(); 
          }
        } catch (_) {} 
      },
      next:     handleNext,
      previous: handlePrevious,
    });
  }, [registerActions, handleNext, handlePrevious, playerReady]);

  // ── YT events ────────────────────────────────────────────────────────────
  const onReady = useCallback((e) => {
    setPlayerReady(true);
    try { 
      e.target.setVolume(volume);
      e.target.unMute();
      // Start playback
      setTimeout(() => { 
        try { 
          e.target.playVideo(); 
          playAttemptRef.current++;
        } catch (_) {
        } 
      }, 500); 
    } catch (_) {}
  }, [volume, currentSong?.title]);

  const onStateChange = useCallback((e) => {
    if (!mountedRef.current) return;
    const YT = window.YT?.PlayerState; 
    if (!YT) return;
    
    switch (e.data) {
      case YT.PLAYING:
        safeSet(() => { 
          setIsPlaying(true); 
          setLoading(false); 
          setError(false); 
          setErrorMsg('');
          retryRef.current = 0; 
        });
        if (onPlayStateChange) onPlayStateChange(true);
        startTracking(); 
        if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
        break;
        
      case YT.PAUSED:
        safeSet(() => { setIsPlaying(false); setLoading(false); });
        if (onPlayStateChange) onPlayStateChange(false);
        stopTracking(); 
        break;
        
      case YT.BUFFERING:
        safeSet(() => setLoading(true)); 
        break;
        
      case YT.ENDED:
        stopTracking(); 
        handleNext(); 
        break;
        
      case YT.UNSTARTED:
        // Try to play again
        setTimeout(() => { 
          try { 
            if (playerRef.current && playerReady) {
              playerRef.current.playVideo(); 
              playAttemptRef.current++;
            }
          } catch (_) {} 
        }, 1000); 
        break;
        
      case -1: // YT.UNSTARTED (sometimes -1)
        setTimeout(() => { 
          try { 
            if (playerRef.current) {
              playerRef.current.playVideo(); 
            }
          } catch (_) {} 
        }, 1000); 
        break;
        
      default: 
        break;
    }
  }, [onPlayStateChange, startTracking, stopTracking, handleNext, playerReady, currentSong?.title]);

  const onError = useCallback((e) => {
    if (!mountedRef.current) return;
    
    console.log('❌ YouTube error code:', e.data, 'for:', currentSong?.title);
    console.log('Retry count:', retryRef.current);
    
    stopTracking();
    
    // Error codes that should skip immediately
    const skipCodes = [100, 101, 150];
    // Error codes that can be retried
    const retryCodes = [2, 5];
    
    if (skipCodes.includes(e.data)) {
      // Video not found / removed / embedding disabled
      safeSet(() => { 
        setError(true); 
        setErrorMsg('This video is unavailable'); 
        setLoading(false); 
      });
      setTimeout(() => handleNext(), 1500);
    } 
    else if (retryCodes.includes(e.data) && retryRef.current < 3) {
      // Network error / HTML5 error - retry
      retryRef.current++;
      safeSet(() => { setLoading(true); setError(false); });
      setTimeout(() => { 
        try { 
          if (playerRef.current) {
            playerRef.current.playVideo(); 
          }
        } catch (_) {} 
      }, 2000);
    } 
    else if (playAttemptRef.current < 3 && retryRef.current < 5) {
      // Unknown error - try to reload
      retryRef.current++;
      playAttemptRef.current++;
      safeSet(() => { setLoading(true); setError(false); });
      setTimeout(() => { 
        try { 
          if (playerRef.current) {
            playerRef.current.loadVideoById(currentSong.id);
            setTimeout(() => {
              playerRef.current?.playVideo();
            }, 1000);
          }
        } catch (_) {} 
      }, 2000);
    } 
    else {
      // Max retries reached - skip
      safeSet(() => { 
        setError(true); 
        setErrorMsg('Cannot play this song'); 
        setLoading(false); 
      });
      setTimeout(() => handleNext(), 1500);
    }
  }, [handleNext, stopTracking, currentSong]);

  // ── DRAG TO SEEK FUNCTIONALITY ───────────────────────────────────────────
  
  const updateProgressFromEvent = useCallback((e) => {
    if (!progressRef.current || !duration) return;
    
    // Prevent default to avoid scrolling on touch devices
    if (e.type === 'touchmove') {
      e.preventDefault();
    }
    
    const rect = progressRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    
    // Update visual immediately
    safeSet(() => {
      setProgress(pct * 100);
      setCurrentTime(pct * duration);
    });
    
    // Seek in player
    try {
      playerRef.current?.seekTo(pct * duration, true);
    } catch (_) {}
  }, [duration]);

  const handleProgressStart = useCallback((e) => {
    if (!duration || !progressRef.current) return;
    
    // Prevent default for touch events
    if (e.type === 'touchstart') {
      e.preventDefault();
    }
    
    isDraggingRef.current = true;
    
    // Prevent text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    
    // Update position immediately
    updateProgressFromEvent(e);
    
    // Add global listeners for drag movement
    document.addEventListener('mousemove', handleProgressMove);
    document.addEventListener('mouseup', handleProgressEnd);
    document.addEventListener('touchmove', handleProgressMove, { passive: false });
    document.addEventListener('touchend', handleProgressEnd);
  }, [duration, updateProgressFromEvent]);

  const handleProgressMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    updateProgressFromEvent(e);
  }, [updateProgressFromEvent]);

  const handleProgressEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    
    // Restore text selection
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    
    // Remove global listeners
    document.removeEventListener('mousemove', handleProgressMove);
    document.removeEventListener('mouseup', handleProgressEnd);
    document.removeEventListener('touchmove', handleProgressMove);
    document.removeEventListener('touchend', handleProgressEnd);
  }, [handleProgressMove]);

  // Clean up drag event listeners on unmount
  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.removeEventListener('mousemove', handleProgressMove);
      document.removeEventListener('mouseup', handleProgressEnd);
      document.removeEventListener('touchmove', handleProgressMove);
      document.removeEventListener('touchend', handleProgressEnd);
    };
  }, [handleProgressMove, handleProgressEnd]);

  // ── init player ──────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    safeSet(() => { 
      setLoading(true); 
      setError(false); 
      setErrorMsg('');
      setProgress(0); 
      setCurrentTime(0);
      setPlayerReady(false);
    });
    stopTracking();
    
    // Start timeout timer
    startSkipTimer();

    const loadAPI = () => new Promise(res => {
      if (window.YT?.Player) { 
        res(); 
        return; 
      }
      if (window.onYouTubeIframeAPIReady) {
        // API already loading, wait for it
        const checkAPI = setInterval(() => {
          if (window.YT?.Player) {
            clearInterval(checkAPI);
            res();
          }
        }, 200);
        return;
      }
      window.onYouTubeIframeAPIReady = () => {
        res();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const t = document.createElement('script'); 
        t.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(t);
      }
    });

    (async () => {
      try {
        await loadAPI();
        if (!mountedRef.current || !containerRef.current) return;
        
        // Destroy previous player
        try { playerRef.current?.destroy(); } catch (_) {}
        
        playerRef.current = new window.YT.Player(containerRef.current, {
          height: '0', 
          width: '0', 
          videoId: currentSong.id,
          playerVars: { 
            autoplay: 1, 
            controls: 0, 
            disablekb: 1, 
            enablejsapi: 1, 
            fs: 0, 
            modestbranding: 1, 
            playsinline: 1, 
            rel: 0, 
            iv_load_policy: 3, 
            origin: window.location.origin,
            hl: 'en',
            cc_load_policy: 0,
          },
          events: { onReady, onStateChange, onError },
        });
      } catch (err) { 
        console.error('Player init error:', err);
        if (mountedRef.current) { 
          safeSet(() => {
            setError(true); 
            setErrorMsg('Player initialization failed');
            setLoading(false);
          });
          setTimeout(() => handleNext(), 2000);
        } 
      }
    })();

    return () => {
      mountedRef.current = false;
      stopTracking();
      if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
      try { playerRef.current?.destroy(); } catch (_) {}
    };
  }, [currentSong.id]);

  // ── controls ─────────────────────────────────────────────────────────────
  const togglePlay = () => {
    if (!playerReady) return;
    try { 
      if (isPlaying) {
        playerRef.current?.pauseVideo(); 
      } else {
        playerRef.current?.playVideo(); 
      }
    } catch (_) {}
  };

  const handleVolumeChange = (e) => {
    const v = Number(e.target.value); 
    setVolume(v); 
    setMuted(v === 0);
    try { playerRef.current?.setVolume(v); } catch (_) {}
  };

  const toggleMute = () => {
    const nm = !muted; 
    setMuted(nm);
    try { 
      if (nm) playerRef.current?.mute(); 
      else { 
        playerRef.current?.unMute(); 
        playerRef.current?.setVolume(volume); 
      } 
    } catch (_) {}
  };

  const handleLike = () => { const s = toggleLike(currentSong); setLiked(s); };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  const queueSongs = songList.slice(currentIndex + 1, currentIndex + 6);

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="ap-overlay" onClick={onClose}>
      <div className="ap-card" onClick={e => e.stopPropagation()}>

        {/* Top buttons */}
        <div className="ap-top-btns">
          <button className="ap-icon-btn" onClick={onMinimize} title="Minimize"><Minus size={15} /></button>
          <button className="ap-icon-btn ap-close" onClick={onClose} title="Close"><X size={15} /></button>
        </div>

        {/* Header */}
        <div className="ap-header">
          <div className={`ap-thumb-wrap${loading ? ' ap-pulse' : ''}`}>
            <img src={currentSong.thumbnail} alt={currentSong.title} className="ap-thumb" />
            {loading && <div className="ap-thumb-overlay"><div className="ap-spin" /></div>}
          </div>
          <div className="ap-info">
            <h3 className="ap-title">{currentSong.title}</h3>
            <p className="ap-channel">{currentSong.channelTitle}</p>
            {currentSong.viewCount && (
              <p className="ap-views">{parseInt(currentSong.viewCount).toLocaleString()} views</p>
            )}
            <div className="ap-song-actions">
              <button className={`ap-like-btn${liked ? ' ap-like-btn--active' : ''}`} onClick={handleLike}>
                <Heart size={15} fill={liked ? '#ff7e3a' : 'none'} color={liked ? '#ff7e3a' : '#9ca3af'} />
                <span>{liked ? 'Liked' : 'Like'}</span>
              </button>

              <button className="ap-add-btn" onClick={() => setShowAddMenu(v => !v)}>
                <ListPlus size={15} color="#9ca3af" />
                <span>Add to Playlist</span>
              </button>
            </div>
          </div>
        </div>

        {showAddMenu && (
          <PlaylistDropdown
            song={currentSong}
            onClose={() => setShowAddMenu(false)}
            onAdded={(name) => showToast(`✓ Added to "${name}"`)}
          />
        )}

        {toast && <div className="ap-toast">{toast}</div>}

        <div ref={containerRef} style={{ display: 'none' }} />

        {/* Better error message */}
        {error && (
          <div className="ap-error">
            {errorMsg || '⚠️ This song can\'t be played'}
            <span className="ap-error-skip"> Skipping to next...</span>
          </div>
        )}

        {/* Progress Bar with DRAG TO SEEK */}
        <div className="ap-progress-area">
          <span className="ap-time">{formatTime(currentTime)}</span>
          <div 
            className="ap-progress-bar" 
            ref={progressRef} 
            onMouseDown={handleProgressStart}
            onTouchStart={handleProgressStart}
          >
            <div 
              className="ap-progress-fill" 
              style={{ width: `${progress}%` }}
            />
            {/* Draggable thumb */}
            <div 
              className="ap-progress-thumb"
              style={{ left: `${progress}%` }}
              onMouseDown={handleProgressStart}
              onTouchStart={handleProgressStart}
            />
          </div>
          <span className="ap-time">{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="ap-controls">
          <button className="ap-ctrl-btn" onClick={handlePrevious}><SkipBack size={18} /></button>
          <button 
            className="ap-play-btn" 
            onClick={togglePlay} 
            disabled={loading && !error}
          >
            {loading && !error ? (
              <div className="ap-spin" />
            ) : isPlaying ? (
              <Pause size={20} fill="white" />
            ) : (
              <Play size={20} fill="white" />
            )}
          </button>
          <button className="ap-ctrl-btn" onClick={handleNext}><SkipForward size={18} /></button>

          <div className="ap-vol-wrap">
            <button className="ap-ctrl-btn" onClick={toggleMute}>
              {muted || volume === 0 ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={muted ? 0 : volume} 
              onChange={handleVolumeChange} 
              className="ap-vol-slider" 
            />
          </div>

          <button 
            className={`ap-ctrl-btn${showQueue ? ' ap-ctrl-active' : ''}`} 
            onClick={() => setShowQueue(v => !v)} 
            title="Queue"
          >
            <ChevronDown size={17} />
          </button>
        </div>

        {/* Queue */}
        {showQueue && (
          <div className="ap-queue">
            <p className="ap-queue-title">Up Next</p>
            {queueSongs.length > 0 ? queueSongs.map(song => (
              <div 
                key={song.id} 
                className="ap-queue-item" 
                onClick={() => onNext(song)}
              >
                <img 
                  src={song.thumbnail} 
                  alt={song.title} 
                  className="ap-queue-thumb" 
                />
                <div className="ap-queue-info">
                  <p className="ap-queue-name">{song.title}</p>
                  <p className="ap-queue-ch">{song.channelTitle}</p>
                </div>
              </div>
            )) : (
              <p className="ap-no-queue">No more songs</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;